import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Switch,
  Text,
  VStack,
  HStack,
  Spinner,
  FormControl,
  FormLabel,
  Divider,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import {
  getCatalogRoles,
  getRoleCatalog,
  saveRoleCatalog,
} from '../actions/privilegeActions';
import { useAppToast } from '../hooks/useAppToast';

/**
 * Privilege Catalog — super_admin only.
 * Defines which privilege options appear for each role in Privilege Management.
 */
const PrivilegeCatalog = () => {
  const appToast = useAppToast();
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role === 'super_admin';

  const [roles, setRoles] = useState([]);
  const [targetRole, setTargetRole] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [options, setOptions] = useState({});
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await getCatalogRoles();
        if (!cancelled) {
          setRoles(list);
          if (list.length) {
            setTargetRole(list[0].role);
          }
        }
      } catch (err) {
        appToast.showApiError(err, 'Failed to load roles');
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const loadCatalog = useCallback(async () => {
    if (!targetRole || !isSuperAdmin) return;
    setLoading(true);
    try {
      const data = await getRoleCatalog(targetRole);
      setCatalog(data.catalog || []);
      setOptions(data.options || {});
      setDirty(false);
    } catch (err) {
      appToast.showApiError(err, 'Failed to load privilege catalog');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRole, isSuperAdmin]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const handleRoleChange = (nextRole) => {
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) {
      return;
    }
    setTargetRole(nextRole);
  };

  const handleToggle = (key, checked) => {
    setOptions((prev) => ({ ...prev, [key]: checked }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!targetRole) return;
    setSaving(true);
    try {
      await saveRoleCatalog({ targetRole, options });
      setDirty(false);
      appToast.showSuccess('Privilege catalog saved');
    } catch (err) {
      appToast.showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box p={4}>
        <Text>Privilege Catalog is available to Super Admin only.</Text>
      </Box>
    );
  }

  return (
    <Box maxW="1000px" mx="auto">
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
        <Box>
          <Heading size="lg">Privilege Catalog</Heading>
          <Text fontSize="sm" color="gray.600" mt={1}>
            Choose which privilege options are available for each role. These options then appear in
            Privilege Management.
          </Text>
        </Box>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={saving}
          isDisabled={!dirty || !targetRole}
        >
          Save Changes
        </Button>
      </Flex>

      <HStack spacing={4} mb={6} align="flex-end" flexWrap="wrap">
        <FormControl maxW="280px">
          <FormLabel mb={1}>Role</FormLabel>
          {rolesLoading ? (
            <Spinner size="sm" />
          ) : (
            <Select
              value={targetRole}
              onChange={(e) => handleRoleChange(e.target.value)}
              placeholder="Select role"
            >
              {roles.map((r) => (
                <option key={r.role} value={r.role}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </FormControl>
      </HStack>

      {targetRole && (
        <Heading size="md" mb={4} pb={2} borderBottom="2px solid" borderColor="blue.400">
          {roles.find((r) => r.role === targetRole)?.label || targetRole}
        </Heading>
      )}

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner size="lg" />
        </Flex>
      ) : !targetRole ? (
        <Text color="gray.600">Select a role to configure its privilege catalog.</Text>
      ) : (
        <VStack spacing={4} align="stretch">
          {catalog.map((group) => (
            <Box
              key={group.group}
              bg={group.color}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
              p={4}
            >
              <Text fontWeight="bold" mb={3} fontSize="sm" color="gray.600" textTransform="uppercase">
                {group.group}
              </Text>
              <Divider mb={3} />
              <VStack spacing={2} align="stretch">
                {group.items.map((item) => {
                  const checked = Boolean(options[item.key]);
                  return (
                    <Flex key={item.key} justify="space-between" align="center" py={1}>
                      <Text fontSize="md">{item.label}</Text>
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500">
                          Off
                        </Text>
                        <Switch
                          colorScheme="blue"
                          isChecked={checked}
                          onChange={(e) => handleToggle(item.key, e.target.checked)}
                        />
                        <Text fontSize="xs" color="gray.500">
                          ON
                        </Text>
                      </HStack>
                    </Flex>
                  );
                })}
              </VStack>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default PrivilegeCatalog;
